import {
  type BranchChangeset,
  type BranchChangesets,
  type ChangesetHistory,
  type ConfigurationChangeset,
  type ConfigurationPackage,
  isLargeOperation,
  isParameterValueAmendOperation,
  isParameterValueCreateOperation,
} from '@festina/configuration-general-types/configuration'
import { notEmpty } from '@festina-life/core'

import { downloadJsonFile } from '../../../util/downloadJsonFile'

export const excludeParameterValueOperationsFromConfigurationPackage = (
  configurationPackage: ConfigurationPackage,
): ConfigurationPackage => {
  const { filteredChangesets, removedOperationKeys } =
    clearParameterValueOperationsFromChangesets(
      configurationPackage.changesets?.filter(notEmpty) ?? [],
    )

  const filteredChangesetHistories = clearOperationsFromChangesetHistories(
    configurationPackage.changesetHistories?.filter(notEmpty) ?? [],
    removedOperationKeys,
  )

  return {
    ...configurationPackage,
    changesets: filteredChangesets,
    changesetHistories: filteredChangesetHistories,
  }
}

export const excludeParameterValueOperationsFromBranchChangesets = (
  branchChangesets: BranchChangesets,
) => ({
  ...branchChangesets,
  branchChangesets: branchChangesets.branchChangesets
    ?.filter(notEmpty)
    .map((branchChangeset) => {
      const { filteredChangesets, removedOperationKeys } =
        clearParameterValueOperationsFromChangesets(
          branchChangeset.changeset ? [branchChangeset.changeset] : [],
        )
      const filteredChangesetHistories = clearOperationsFromChangesetHistories(
        branchChangeset.changesetHistory
          ? [branchChangeset.changesetHistory]
          : [],
        removedOperationKeys,
      )

      return {
        ...branchChangeset,
        changeset: filteredChangesets.at(0),
        changesetHistory: filteredChangesetHistories.at(0),
      } satisfies BranchChangeset
    }),
})

export const downloadConfigurationFile = (
  packageData: ConfigurationPackage | BranchChangesets,
  packageName: string,
) => downloadJsonFile(packageData, packageName)

const clearParameterValueOperationsFromChangesets = (
  changesets: ConfigurationChangeset[],
) => {
  const removedOperationKeys = new Set<string>()
  const filteredChangesets = changesets?.filter(notEmpty).map((changeset) => ({
    ...changeset,
    operations: changeset.operations?.filter((operation) => {
      const isValidOperation =
        !isParameterValueCreateOperation(operation) &&
        !isParameterValueAmendOperation(operation) &&
        !(
          isLargeOperation(operation) &&
          operation.innerOperationType === 'parameterValueCreate'
        )

      if (!isValidOperation && operation.operationKey) {
        removedOperationKeys.add(operation.operationKey)
      }

      return isValidOperation
    }),
  }))

  return { filteredChangesets, removedOperationKeys }
}

const clearOperationsFromChangesetHistories = (
  changesetHistories: ChangesetHistory[],
  removedOperationKeys: Set<string>,
) =>
  changesetHistories?.filter(notEmpty).map((changesetHistory) => ({
    ...changesetHistory,
    approvalSets: changesetHistory.approvalSets
      ?.filter(notEmpty)
      .map((approvalSet) => ({
        ...approvalSet,
        operationKeys: approvalSet.operationKeys
          ?.filter(notEmpty)
          .filter((operationKey) => !removedOperationKeys.has(operationKey)),
      })),
    originSets: changesetHistory.originSets
      ?.filter(notEmpty)
      .map((originSet) => ({
        ...originSet,
        operationKeys: originSet.operationKeys
          ?.filter(notEmpty)
          .filter((operationKey) => !removedOperationKeys.has(operationKey)),
      })),
  }))
